import React, { FC, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  MutationFunctionOptions,
  OperationVariables,
  DefaultContext,
  ApolloCache,
} from "@apollo/client";

// Define a type for the behaviour rules
interface BehaviourRules {
  informOperatorImmediately: boolean;
  informCustomer: boolean;
  remedialActionMinutes: number;
  applyNewAreaTariff: boolean;
  applyPenalty: boolean;
  penaltyAmount: number | string;
}

// Define a type for a behaviour that has public, permanent, and visitor parker rules
interface ParkerSpecificBehaviour {
  public: BehaviourRules;
  permanent: BehaviourRules;
  visitor: BehaviourRules;
}

// Define a type for all site behaviours
interface SiteBehaviours {
  wrongAreaBay: ParkerSpecificBehaviour;
  overstays: ParkerSpecificBehaviour;
  noValidPaymentMethod: ParkerSpecificBehaviour;
  outsideAllowedAccessHours: ParkerSpecificBehaviour;
  reEnteredWithoutExiting: ParkerSpecificBehaviour;
}

type BehaviourTypeKey = keyof SiteBehaviours;

// Define the site type to match what's passed from Site.tsx
type SiteType = {
  id: string;
  behaviourConfig?: string | null;
  notificationEmails?: string | null;
  notificationPhones?: string | null;
  immediateNotifications?: boolean | null;
  gracePeriod?: number | null;
  // Add other site properties as needed
};

// Simplified props interface
interface BehavioursProps {
  site: SiteType;
  onSave: (
    options?: MutationFunctionOptions<
      any,
      OperationVariables,
      DefaultContext,
      ApolloCache<any>
    >
  ) => Promise<any>;
  isSaving: boolean;
  canEdit: boolean;
}

const Behaviours: FC<BehavioursProps> = ({
  site,
  onSave,
  isSaving,
  canEdit,
}) => {
  // Helper to create default rules
  const createDefaultRules = (): BehaviourRules => ({
    informOperatorImmediately: false,
    informCustomer: false,
    remedialActionMinutes: 0,
    applyNewAreaTariff: false,
    applyPenalty: false,
    penaltyAmount: 0,
  });
  const getDefaultSiteBehaviours = useCallback(
    (): SiteBehaviours => ({
      wrongAreaBay: {
        public: createDefaultRules(),
        permanent: createDefaultRules(),
        visitor: createDefaultRules(),
      },
      overstays: {
        public: createDefaultRules(),
        permanent: createDefaultRules(),
        visitor: createDefaultRules(),
      },
      noValidPaymentMethod: {
        public: createDefaultRules(),
        permanent: createDefaultRules(),
        visitor: createDefaultRules(),
      },
      outsideAllowedAccessHours: {
        public: createDefaultRules(),
        permanent: createDefaultRules(),
        visitor: createDefaultRules(),
      },
      reEnteredWithoutExiting: {
        public: createDefaultRules(),
        permanent: createDefaultRules(),
        visitor: createDefaultRules(),
      },
    }),
    []
  );

  const [behaviours, setBehaviours] = useState<SiteBehaviours>(
    getDefaultSiteBehaviours()
  );
  const [showHelpText, setShowHelpText] = useState(true);

  // State for notification settings
  const [notificationEmails, setNotificationEmails] = useState<string>("");
  const [notificationPhones, setNotificationPhones] = useState<string>("");

  // Initialize state when site changes
  useEffect(() => {
    // Initialize behaviour config
    if (site.behaviourConfig) {
      try {
        const parsedConfig = JSON.parse(site.behaviourConfig);
        if (parsedConfig.wrongAreaBay && parsedConfig.overstays) {
          // Ensure all parker types exist, add visitor if missing
          const updatedConfig = {
            wrongAreaBay: {
              public: parsedConfig.wrongAreaBay.public || createDefaultRules(),
              permanent:
                parsedConfig.wrongAreaBay.permanent || createDefaultRules(),
              visitor:
                parsedConfig.wrongAreaBay.visitor || createDefaultRules(),
            },
            overstays: {
              public: parsedConfig.overstays.public || createDefaultRules(),
              permanent:
                parsedConfig.overstays.permanent || createDefaultRules(),
              visitor: parsedConfig.overstays.visitor || createDefaultRules(),
            },
            noValidPaymentMethod: {
              public:
                parsedConfig.noValidPaymentMethod?.public ||
                createDefaultRules(),
              permanent:
                parsedConfig.noValidPaymentMethod?.permanent ||
                createDefaultRules(),
              visitor:
                parsedConfig.noValidPaymentMethod?.visitor ||
                createDefaultRules(),
            },
            outsideAllowedAccessHours: {
              public:
                parsedConfig.outsideAllowedAccessHours?.public ||
                createDefaultRules(),
              permanent:
                parsedConfig.outsideAllowedAccessHours?.permanent ||
                createDefaultRules(),
              visitor:
                parsedConfig.outsideAllowedAccessHours?.visitor ||
                createDefaultRules(),
            },
            reEnteredWithoutExiting: {
              public:
                parsedConfig.reEnteredWithoutExiting?.public ||
                createDefaultRules(),
              permanent:
                parsedConfig.reEnteredWithoutExiting?.permanent ||
                createDefaultRules(),
              visitor:
                parsedConfig.reEnteredWithoutExiting?.visitor ||
                createDefaultRules(),
            },
          };
          setBehaviours(updatedConfig);
        } else {
          console.warn(
            "Parsed behaviourConfig does not match expected structure, using defaults."
          );
          setBehaviours(getDefaultSiteBehaviours());
        }
      } catch (error) {
        console.error("Failed to parse behaviourConfig:", error);
        setBehaviours(getDefaultSiteBehaviours());
      }
    } else {
      setBehaviours(getDefaultSiteBehaviours());
    }

    // Initialize notification states
    setNotificationEmails(site.notificationEmails ?? "");
    setNotificationPhones(site.notificationPhones ?? "");
  }, [
    getDefaultSiteBehaviours,
    site.behaviourConfig,
    site.notificationEmails,
    site.notificationPhones,
  ]);

  const handleBehaviourChange = (
    behaviourType: BehaviourTypeKey,
    parkerType: "public" | "permanent" | "visitor",
    field: keyof BehaviourRules,
    value: any
  ) => {
    setBehaviours((prev) => ({
      ...prev,
      [behaviourType]: {
        ...prev[behaviourType],
        [parkerType]: {
          ...prev[behaviourType][parkerType],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const behaviourConfigString = JSON.stringify(behaviours);

    try {
      // Call the updateSite mutation with the correct Apollo mutation function format
      await onSave({
        variables: {
          site: {
            ...site,
            areas: [], // Assuming areas are not being updated here
            behaviourConfig: behaviourConfigString,
            notificationEmails: notificationEmails || null,
            notificationPhones: notificationPhones || null,
          },
        },
      });

      toast.success("Behaviours and notifications saved successfully!");
    } catch (error) {
      console.error("Failed to save behaviours and notifications:", error);
      toast.error("Failed to save settings.");
    }
  };

  const renderBehaviourRuleSet = (
    behaviourType: BehaviourTypeKey,
    parkerType: "public" | "permanent" | "visitor"
  ) => {
    const rules = behaviours[behaviourType][parkerType];
    const prefix = `${behaviourType}-${parkerType}-`;
    const minGracePeriod = site.gracePeriod || 0;
    const isRemedialActionTooLow = rules.remedialActionMinutes < minGracePeriod;

    // For Overstay Public Parkers, only show Detection section
    // For Visitors, only show Detection section (no Remediation or Escalation)
    // For No Valid Payment Method, only show Detection section for all parker types
    // For Outside Allowed Access Hours, only show Detection section for all parker types
    // For Re-entered Without Exiting, only show Detection section for all parker types
    const showOnlyDetection =
      (behaviourType === "overstays" && parkerType === "public") ||
      parkerType === "visitor" ||
      behaviourType === "noValidPaymentMethod" ||
      behaviourType === "outsideAllowedAccessHours" ||
      behaviourType === "reEnteredWithoutExiting";

    return (
      <>
        {/* Detection Section */}
        <div
          className="p-3 mb-3 border border-success rounded"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <h5 className="text-success">Detection</h5>
          <div className="mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id={`${prefix}informOperatorImmediately`}
                checked={rules.informOperatorImmediately}
                onChange={(e) =>
                  handleBehaviourChange(
                    behaviourType,
                    parkerType,
                    "informOperatorImmediately",
                    e.target.checked
                  )
                }
                disabled={!canEdit}
              />
              <label
                className="form-check-label"
                htmlFor={`${prefix}informOperatorImmediately`}
              >
                Operator(s) informed immediately
              </label>
            </div>
            {showHelpText && (
              <small className="form-text text-muted d-block">
                Sends an automated SMS/Email notification to all configured
                operators.
              </small>
            )}
          </div>
          <div className="mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id={`${prefix}informCustomer`}
                checked={rules.informCustomer}
                onChange={(e) =>
                  handleBehaviourChange(
                    behaviourType,
                    parkerType,
                    "informCustomer",
                    e.target.checked
                  )
                }
                disabled={!canEdit}
              />
              <label
                className="form-check-label"
                htmlFor={`${prefix}informCustomer`}
              >
                Customer is informed immediately
              </label>
            </div>
            {showHelpText && (
              <small className="form-text text-muted d-block">
                Sends an automated SMS/Email notification to the customer.
              </small>
            )}
          </div>
        </div>

        {/* Only show Remediation section if NOT Overstay Public Parkers */}
        {!showOnlyDetection && (
          <div
            className="p-3 mb-3 border border-warning rounded"
            style={{ backgroundColor: "#fffdf5" }}
          >
            <h5 className="text-warning">Remediation</h5>
            <div className="mb-3">
              <label
                htmlFor={`${prefix}remedialActionMinutes`}
                className="form-label"
              >
                Minutes to allow for remedial action
              </label>
              <input
                type="number"
                className={`form-control ${
                  isRemedialActionTooLow ? "is-invalid" : ""
                }`}
                id={`${prefix}remedialActionMinutes`}
                value={rules.remedialActionMinutes}
                onChange={(e) =>
                  handleBehaviourChange(
                    behaviourType,
                    parkerType,
                    "remedialActionMinutes",
                    parseInt(e.target.value, 10) || 0
                  )
                }
                min={site.gracePeriod || 0}
                readOnly={!canEdit}
              />
              {isRemedialActionTooLow && (
                <div className="invalid-feedback">
                  Must be at least {minGracePeriod} minutes (site grace period).
                </div>
              )}
              {showHelpText && (
                <small className="form-text text-muted">
                  Time (in minutes) given to the parker to correct the behaviour
                  before escalation. Must be at least {site.gracePeriod || 0}{" "}
                  minutes (site grace period).
                </small>
              )}
            </div>
          </div>
        )}

        {/* Only show Escalation section for Permanent parkers and Public parkers on Wrong Area Bay */}
        {!showOnlyDetection && (
          <div
            className="p-3 mb-3 border border-danger rounded"
            style={{ backgroundColor: "#fef7f7" }}
          >
            <h5 className="text-danger">Escalation</h5>
            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`${prefix}applyNewAreaTariff`}
                  checked={rules.applyNewAreaTariff}
                  onChange={(e) =>
                    handleBehaviourChange(
                      behaviourType,
                      parkerType,
                      "applyNewAreaTariff",
                      e.target.checked
                    )
                  }
                  disabled={!canEdit}
                />
                <label
                  className="form-check-label"
                  htmlFor={`${prefix}applyNewAreaTariff`}
                >
                  Apply relevant area tariff
                </label>
              </div>
              {showHelpText && (
                <small className="form-text text-muted d-block">
                  If the parker moved to a different tariff area, charge them
                  according to the new area's rates.
                </small>
              )}
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`${prefix}applyPenalty`}
                  checked={rules.applyPenalty}
                  onChange={(e) =>
                    handleBehaviourChange(
                      behaviourType,
                      parkerType,
                      "applyPenalty",
                      e.target.checked
                    )
                  }
                  disabled={!canEdit}
                />
                <label
                  className="form-check-label"
                  htmlFor={`${prefix}applyPenalty`}
                >
                  Apply a penalty
                </label>
              </div>
              {showHelpText && (
                <small className="form-text text-muted d-block">
                  Charges the parker a fixed penalty amount if the behaviour is
                  not remediated.
                </small>
              )}
            </div>

            {rules.applyPenalty && (
              <div className="mb-3">
                <label
                  htmlFor={`${prefix}penaltyAmount`}
                  className="form-label"
                >
                  Penalty amount ($)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id={`${prefix}penaltyAmount`}
                  value={rules.penaltyAmount}
                  onChange={(e) =>
                    handleBehaviourChange(
                      behaviourType,
                      parkerType,
                      "penaltyAmount",
                      parseFloat(e.target.value) || ""
                    )
                  }
                  min="0"
                  step="0.01"
                  disabled={!rules.applyPenalty || !canEdit}
                />
                {showHelpText && (
                  <small className="form-text text-muted">
                    The fixed amount to charge as a penalty.
                  </small>
                )}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Behaviour Rules & Notifications</h3>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="toggleHelpTextBehaviours"
            checked={showHelpText}
            onChange={() => setShowHelpText(!showHelpText)}
          />
          <label
            className="form-check-label"
            htmlFor="toggleHelpTextBehaviours"
          >
            Show Help Text
          </label>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Notifications Card */}
        <div className="card mb-3">
          <div className="card-header">
            <h4>Notifications (Abnormal Events)</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="behaviourNotificationEmails">
                    Operator Email Addresses (semicolon separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="behaviourNotificationEmails"
                    value={notificationEmails}
                    onChange={(e) => setNotificationEmails(e.target.value)}
                    readOnly={!canEdit}
                  />
                  {showHelpText && (
                    <small className="form-text text-muted">
                      Contacts to notify for abnormal events or other site
                      alerts.
                    </small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="behaviourNotificationPhones">
                    Phone Numbers (semicolon separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="behaviourNotificationPhones"
                    value={notificationPhones}
                    onChange={(e) => setNotificationPhones(e.target.value)}
                    readOnly={!canEdit}
                  />
                  {showHelpText && (
                    <small className="form-text text-muted">
                      Phone numbers for SMS alerts for abnormal events.
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Behaviour Rules Cards */}
        <div className="card mb-3">
          <div className="card-header">
            <h4>1. Parker parks in wrong Area/Bay</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Public Parkers</h5>
                {renderBehaviourRuleSet("wrongAreaBay", "public")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Permanent Parkers</h5>
                {renderBehaviourRuleSet("wrongAreaBay", "permanent")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Visitors</h5>
                {renderBehaviourRuleSet("wrongAreaBay", "visitor")}
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header">
            <h4>2. Overstay Behaviour</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Public Parkers</h5>
                {renderBehaviourRuleSet("overstays", "public")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Permanent Parkers</h5>
                {renderBehaviourRuleSet("overstays", "permanent")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Visitors</h5>
                {renderBehaviourRuleSet("overstays", "visitor")}
              </div>
            </div>
          </div>{" "}
        </div>

        <div className="card mb-3">
          <div className="card-header">
            <h4>3. No Valid Payment Method</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3 text-center">For Public Parkers</h5>
                {renderBehaviourRuleSet("noValidPaymentMethod", "public")}
              </div>
              <div className="col-md-6">
                <h5 className="mb-3 text-center">For Permanent Parkers</h5>
                {renderBehaviourRuleSet("noValidPaymentMethod", "permanent")}
              </div>
            </div>
            {showHelpText && (
              <div className="mt-3">
                <small className="form-text text-muted">
                  <strong>Note:</strong> Visitors do not require a valid payment
                  method, so this behavior does not apply to them.
                </small>
              </div>
            )}
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header">
            <h4>4. Attempted Access Outside Allowed Hours</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Public Parkers</h5>
                {renderBehaviourRuleSet("outsideAllowedAccessHours", "public")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Permanent Parkers</h5>
                {renderBehaviourRuleSet(
                  "outsideAllowedAccessHours",
                  "permanent"
                )}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Visitors</h5>
                {renderBehaviourRuleSet("outsideAllowedAccessHours", "visitor")}
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header">
            <h4>5. Parker Re-entered the Site Without Exiting</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Public Parkers</h5>
                {renderBehaviourRuleSet("reEnteredWithoutExiting", "public")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Permanent Parkers</h5>
                {renderBehaviourRuleSet("reEnteredWithoutExiting", "permanent")}
              </div>
              <div className="col-md-4">
                <h5 className="mb-3 text-center">For Visitors</h5>
                {renderBehaviourRuleSet("reEnteredWithoutExiting", "visitor")}
              </div>
            </div>
          </div>
        </div>

        {canEdit && (
          <button
            type="submit"
            className="btn btn-primary mt-3 mb-3"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Behaviour Settings"}
          </button>
        )}
      </form>
    </div>
  );
};

export default Behaviours;
